import BaseApiClient from '../BaseApiClient';
import type {
  FocusArea,
  FocusAreaPayload,
  GoalPayload,
  KeyObjective,
  KeyObjectivePayload,
  ObjectiveGoal,
  ObjectiveProcess,
  PlanTreeResponse,
  ProcessPayload,
  QuarterPlan
} from '@/interfaces/Objectives.interface';
import {
  mockCreateFocusArea,
  mockCreateProcess,
  mockCreateGoal,
  mockCreateKeyObjective,
  mockGetAssignedGoals,
  mockGetAssignedProcesses,
  mockGetPlanTree,
  mockGetQuarterPlans,
  mockUpdateFocusArea,
  mockUpdateGoal,
  mockUpdateKeyObjective,
  mockUpdateProcess
} from './mock-objectives-store';
import { useObjectivesLiveApi } from './use-objectives-live-api';

const API_VERSION = '2026-06-16';

export class ApiObjectives extends BaseApiClient {
  private useMock() {
    return !useObjectivesLiveApi();
  }

  async getQuarterPlans(): Promise<QuarterPlan[]> {
    if (this.useMock()) {
      return Promise.resolve(mockGetQuarterPlans());
    }
    const res = await this.client.get('/quarter-plans', {
      headers: { Version: API_VERSION }
    });
    return res.data.data;
  }

  async getPlanTree(quarterPlanId: number): Promise<PlanTreeResponse> {
    if (this.useMock()) {
      return Promise.resolve(mockGetPlanTree(quarterPlanId));
    }
    const res = await this.client.get(
      `/quarter-plans/${quarterPlanId}/plan-tree`,
      { headers: { Version: API_VERSION } }
    );
    return res.data.data;
  }

  async createFocusArea(payload: FocusAreaPayload): Promise<FocusArea> {
    if (this.useMock()) {
      return Promise.resolve(
        mockCreateFocusArea(payload.quarter_plan_id, payload.name)
      );
    }
    const res = await this.client.post('/focus-areas', payload, {
      headers: { Version: API_VERSION }
    });
    return res.data.data;
  }

  async updateFocusArea(id: number, name: string): Promise<FocusArea> {
    if (this.useMock()) {
      return Promise.resolve(mockUpdateFocusArea(id, name));
    }
    const res = await this.client.patch(
      `/focus-areas/${id}`,
      { name },
      { headers: { Version: API_VERSION } }
    );
    return res.data.data;
  }

  async createKeyObjective(payload: KeyObjectivePayload): Promise<KeyObjective> {
    if (this.useMock()) {
      return Promise.resolve(mockCreateKeyObjective(payload.focus_area_id, payload));
    }
    const res = await this.client.post('/key-objectives', payload, {
      headers: { Version: API_VERSION }
    });
    return res.data.data;
  }

  async updateKeyObjective(
    id: number,
    payload: Partial<KeyObjectivePayload> & { focus_area_id?: number }
  ): Promise<KeyObjective> {
    if (this.useMock()) {
      return Promise.resolve(mockUpdateKeyObjective(id, payload));
    }
    const res = await this.client.patch(`/key-objectives/${id}`, payload, {
      headers: { Version: API_VERSION }
    });
    return res.data.data;
  }

  async createGoal(payload: GoalPayload): Promise<ObjectiveGoal> {
    if (this.useMock()) {
      return Promise.resolve(
        mockCreateGoal(payload.key_objective_id, {
          name: payload.name,
          time_frame: payload.time_frame,
          assignee_user_id: payload.assignee_user_id,
          tracking: payload.tracking
            ? {
                pipeline_id: payload.tracking.pipeline_id,
                milestone_stage_id: payload.tracking.milestone_stage_id,
                target_task_count: payload.tracking.target_task_count,
                team_member_ids: payload.tracking.team_member_ids,
                current_task_count: 0
              }
            : null,
          business_unit_ids: payload.business_unit_ids,
          group_entity_ids: payload.group_entity_ids
        })
      );
    }
    const res = await this.client.post('/goals', payload, {
      headers: { Version: API_VERSION }
    });
    return res.data.data;
  }

  async updateGoal(
    id: number,
    payload: Partial<GoalPayload>
  ): Promise<ObjectiveGoal> {
    if (this.useMock()) {
      return Promise.resolve(
        mockUpdateGoal(id, {
          key_objective_id: payload.key_objective_id,
          name: payload.name,
          time_frame: payload.time_frame,
          assignee_user_id: payload.assignee_user_id,
          tracking: payload.tracking
            ? {
                pipeline_id: payload.tracking.pipeline_id,
                milestone_stage_id: payload.tracking.milestone_stage_id,
                target_task_count: payload.tracking.target_task_count,
                team_member_ids: payload.tracking.team_member_ids,
                current_task_count: payload.tracking.current_task_count ?? 0
              }
            : undefined,
          business_unit_ids: payload.business_unit_ids,
          group_entity_ids: payload.group_entity_ids
        })
      );
    }
    const res = await this.client.patch(`/goals/${id}`, payload, {
      headers: { Version: API_VERSION }
    });
    return res.data.data;
  }

  async createProcess(payload: ProcessPayload): Promise<ObjectiveProcess> {
    if (this.useMock()) {
      return Promise.resolve(
        mockCreateProcess(payload.key_objective_id, payload)
      );
    }
    const res = await this.client.post('/processes', payload, {
      headers: { Version: API_VERSION }
    });
    return res.data.data;
  }

  async updateProcess(
    id: number,
    payload: Partial<ProcessPayload>
  ): Promise<ObjectiveProcess> {
    if (this.useMock()) {
      return Promise.resolve(
        mockUpdateProcess(id, {
          name: payload.name,
          time_frame: payload.time_frame,
          key_objective_id: payload.key_objective_id,
          assignee_user_id: payload.assignee_user_id,
          business_unit_ids: payload.business_unit_ids,
          group_entity_ids: payload.group_entity_ids
        })
      );
    }
    const res = await this.client.patch(`/processes/${id}`, payload, {
      headers: { Version: API_VERSION }
    });
    return res.data.data;
  }

  async getAssignedGoals(): Promise<ObjectiveGoal[]> {
    if (this.useMock()) {
      return Promise.resolve(mockGetAssignedGoals());
    }
    const res = await this.client.get('/goals/assigned-to-me', {
      headers: { Version: API_VERSION }
    });
    return res.data.data;
  }

  async getAssignedProcesses(): Promise<{ id: number; name: string }[]> {
    if (this.useMock()) {
      return Promise.resolve(
        mockGetAssignedProcesses().map((process) => ({
          id: process.id,
          name: process.name
        }))
      );
    }
    const res = await this.client.get('/processes/assigned-to-me', {
      headers: { Version: API_VERSION }
    });
    return res.data.data;
  }
}

export { objectivesQueryKeys } from './query-keys';
